import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import {
  CalendarDays,
  Clock,
  Search,
  Phone,
  AlertTriangle,
  Check,
  X,
  UserX,
  Shield,
  MessageCircle,
  Loader2,
  ChevronRight,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AppointmentRow {
  readonly id: string;
  readonly serviceId: string;
  readonly serviceName: string | null;
  readonly startTime: string;
  readonly endTime: string;
  readonly status: string;
  readonly customerName: string;
  readonly customerPhone: string | null;
  readonly customerEmail: string | null;
  readonly notes: string | null;
  readonly source: string;
  readonly depositStatus: string | null;
  readonly depositAmountAgorot: number | null;
  readonly createdAt: string;
}

interface DepositInfo {
  readonly status: string;
  readonly amountAgorot: number | null;
  readonly daysRemaining: number;
  readonly isExpired: boolean;
  readonly isWarning: boolean;
}

interface ReminderInfo {
  readonly id: string;
  readonly reminderType: string;
  readonly scheduledAt: string;
  readonly sentAt: string | null;
  readonly status: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string }> = {
  confirmed: {
    label: "מאושר",
    bg: "rgba(124,58,237,0.10)",
    text: "var(--brand-primary)",
    border: "rgba(124,58,237,0.25)",
  },
  completed: {
    label: "הושלם",
    bg: "rgba(16,185,129,0.10)",
    text: "#059669",
    border: "rgba(16,185,129,0.25)",
  },
  cancelled: {
    label: "בוטל",
    bg: "rgba(113,113,122,0.10)",
    text: "#71717A",
    border: "rgba(113,113,122,0.20)",
  },
  no_show: {
    label: "לא הגיע",
    bg: "rgba(239,68,68,0.10)",
    text: "#DC2626",
    border: "rgba(239,68,68,0.25)",
  },
};

const REMINDER_TYPE_LABELS: Record<string, string> = {
  "24h": "24 שעות",
  "3h": "3 שעות",
  "1h": "שעה",
};

const REMINDER_STATUS_LABELS: Record<string, string> = {
  pending: "ממתין",
  sent: "נשלח",
  failed: "נכשל",
  cancelled: "בוטל",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("he-IL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// StatusBadge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { readonly status: string }): ReactNode {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG["confirmed"]!;
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border"
      style={{ background: cfg.bg, color: cfg.text, borderColor: cfg.border }}
    >
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// AppointmentsPage
// ---------------------------------------------------------------------------

export function AppointmentsPage(): ReactNode {
  const { accessToken } = useAuth();
  const [appointments, setAppointments] = useState<readonly AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Detail view state
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null);
  const [reminders, setReminders] = useState<readonly ReminderInfo[]>([]);
  const [noShowCount, setNoShowCount] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);
      if (statusFilter) params.set("status", statusFilter);

      const res = await fetch(`/api/booking/bookings?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as {
        success: boolean;
        data: readonly AppointmentRow[];
      };
      if (json.success) {
        setAppointments(json.data);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    void fetchAppointments();
  }, [fetchAppointments]);

  const fetchDetail = useCallback(
    async (id: string, phone: string | null) => {
      try {
        const depRes = await fetch(`/api/booking/bookings/${id}/deposit`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const depJson = (await depRes.json()) as { success: boolean; data: DepositInfo };
        if (depJson.success) setDepositInfo(depJson.data);
      } catch {
        setDepositInfo(null);
      }

      try {
        const remRes = await fetch(`/api/booking/settings/reminders/${id}/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const remJson = (await remRes.json()) as {
          success: boolean;
          data: readonly ReminderInfo[];
        };
        if (remJson.success) setReminders(remJson.data);
      } catch {
        setReminders([]);
      }

      if (phone) {
        try {
          const nsRes = await fetch(
            `/api/booking/bookings/customers/${encodeURIComponent(phone)}/no-shows`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );
          const nsJson = (await nsRes.json()) as {
            success: boolean;
            data: { count: number; isBlocked: boolean };
          };
          if (nsJson.success) setNoShowCount(nsJson.data.count);
        } catch {
          setNoShowCount(null);
        }
      }
    },
    [accessToken],
  );

  const selectAppointment = useCallback(
    (a: AppointmentRow) => {
      setSelectedId(a.id);
      setDepositInfo(null);
      setReminders([]);
      setNoShowCount(null);
      void fetchDetail(a.id, a.customerPhone);
    },
    [fetchDetail],
  );

  const doAction = useCallback(
    async (id: string, action: "complete" | "no_show" | "cancel") => {
      setActionLoading(true);
      try {
        const url =
          action === "cancel"
            ? `/api/booking/bookings/${id}/cancel`
            : action === "no_show"
              ? `/api/booking/bookings/${id}/no-show`
              : `/api/booking/bookings/${id}/status`;

        const body =
          action === "cancel"
            ? {}
            : action === "no_show"
              ? {}
              : { status: "completed" };

        const method = action === "cancel" ? "PUT" : "POST";

        await fetch(url, {
          method: action === "complete" ? "PUT" : method,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        setSelectedId(null);
        void fetchAppointments();
      } finally {
        setActionLoading(false);
      }
    },
    [accessToken, fetchAppointments],
  );

  const doCapture = useCallback(
    async (id: string) => {
      setActionLoading(true);
      try {
        await fetch(`/api/booking/bookings/${id}/deposit/capture`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: "{}",
        });
        void fetchDetail(id, null);
      } finally {
        setActionLoading(false);
      }
    },
    [accessToken, fetchDetail],
  );

  const selected = selectedId
    ? appointments.find((a) => a.id === selectedId) ?? null
    : null;

  const filtered = appointments.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.customerName.toLowerCase().includes(q) ||
      (a.customerPhone?.includes(q) ?? false)
    );
  });

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayAppointments = appointments.filter(
    (a) => a.startTime.slice(0, 10) === todayStr && a.status === "confirmed",
  );

  return (
    <div className="p-4 md:p-6 space-y-4" dir="rtl">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm shrink-0"
          style={{ background: "var(--brand-gradient)" }}
        >
          <CalendarDays className="h-4 w-4 text-white" />
        </div>
        <div>
          <h1
            className="text-lg font-bold leading-tight text-gradient"
            style={{ fontFamily: "var(--font-display)" }}
          >
            תורים
          </h1>
          <p className="text-xs" style={{ color: "hsl(var(--muted-foreground))" }}>
            {appointments.length > 0
              ? `${appointments.length} תורים בסה״כ`
              : "נהל את כל הפגישות"}
          </p>
        </div>
      </div>

      {/* Today highlight */}
      {todayAppointments.length > 0 && (
        <div
          className="rounded-xl p-4 flex items-center gap-3 border"
          style={{
            background: "var(--brand-gradient-soft)",
            borderColor: "rgba(124,58,237,0.20)",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: "var(--brand-gradient)" }}
          >
            <CalendarDays className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold" style={{ color: "hsl(var(--foreground))" }}>
              היום:{" "}
              <span style={{ color: "var(--brand-primary)" }}>
                {todayAppointments.length} תורים
              </span>
            </p>
            {todayAppointments[0] && (
              <p className="text-xs mt-0.5 truncate" style={{ color: "hsl(var(--muted-foreground))" }}>
                הבא: {formatTime(todayAppointments[0].startTime)} —{" "}
                {todayAppointments[0].serviceName}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4"
            style={{ color: "hsl(var(--muted-foreground))" }}
          />
          <Input
            placeholder="חיפוש לפי שם או טלפון..."
            className="ps-9 min-h-[44px]"
            style={{ borderRadius: "var(--radius-md)" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="min-h-[44px] border px-3 text-sm min-w-[140px]"
          style={{
            borderRadius: "var(--radius-md)",
            borderColor: "hsl(var(--border))",
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
          }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">כל הסטטוסים</option>
          <option value="confirmed">מאושר</option>
          <option value="completed">הושלם</option>
          <option value="cancelled">בוטל</option>
          <option value="no_show">לא הגיע</option>
        </select>
        <Input
          type="date"
          className="min-h-[44px] w-[140px]"
          style={{ borderRadius: "var(--radius-md)" }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <Input
          type="date"
          className="min-h-[44px] w-[140px]"
          style={{ borderRadius: "var(--radius-md)" }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ background: "var(--brand-gradient-soft)" }}
            >
              <Loader2
                className="h-6 w-6 animate-spin"
                style={{ color: "var(--brand-primary)" }}
              />
            </div>
            <p className="text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
              טוען תורים...
            </p>
          </div>
        </div>
      ) : selected ? (
        <AppointmentDetail
          appointment={selected}
          depositInfo={depositInfo}
          reminders={reminders}
          noShowCount={noShowCount}
          actionLoading={actionLoading}
          onBack={() => setSelectedId(null)}
          onComplete={() => void doAction(selected.id, "complete")}
          onNoShow={() => void doAction(selected.id, "no_show")}
          onCancel={() => void doAction(selected.id, "cancel")}
          onCapture={() => void doCapture(selected.id)}
        />
      ) : filtered.length === 0 ? (
        <div
          className="rounded-xl border-2 border-dashed flex flex-col items-center justify-center py-16"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: "var(--brand-gradient-soft)" }}
          >
            <CalendarDays
              className="h-7 w-7"
              style={{ color: "var(--brand-primary)", opacity: 0.5 }}
            />
          </div>
          <p className="text-base font-semibold" style={{ color: "hsl(var(--foreground))" }}>
            אין תורים
          </p>
          <p className="text-sm mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>
            לא נמצאו תורים לפי הסינון הנוכחי
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div
            className="hidden md:block overflow-hidden shadow-sm"
            style={{
              borderRadius: "var(--radius-lg)",
              border: "1px solid hsl(var(--border))",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <table className="w-full text-sm">
              <thead
                style={{
                  background: "var(--brand-gradient-soft)",
                  borderBottom: "1px solid hsl(var(--border))",
                }}
              >
                <tr>
                  {["תאריך/שעה", "לקוח", "שירות", "סטטוס", "פיקדון"].map((h) => (
                    <th
                      key={h}
                      className="p-3 text-start text-xs font-semibold uppercase tracking-wide"
                      style={{ color: "var(--brand-primary)" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, idx) => (
                  <tr
                    key={a.id}
                    className="cursor-pointer transition-colors hover:bg-[rgba(124,58,237,0.04)]"
                    style={{
                      borderTop: idx === 0 ? "none" : "1px solid hsl(var(--border))",
                    }}
                    onClick={() => selectAppointment(a)}
                  >
                    <td className="p-3">
                      <span className="font-medium" style={{ color: "hsl(var(--foreground))" }}>
                        {formatDate(a.startTime)}
                      </span>
                      <span
                        className="ms-2 text-xs"
                        style={{ color: "hsl(var(--muted-foreground))" }}
                      >
                        {formatTime(a.startTime)}
                      </span>
                    </td>
                    <td
                      className="p-3 font-medium"
                      style={{ color: "hsl(var(--foreground))" }}
                    >
                      {a.customerName}
                    </td>
                    <td className="p-3" style={{ color: "hsl(var(--muted-foreground))" }}>
                      {a.serviceName ?? "—"}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={a.status} />
                    </td>
                    <td className="p-3">
                      {a.depositStatus === "held" && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border"
                          style={{
                            background: "rgba(245,158,11,0.10)",
                            color: "#B45309",
                            borderColor: "rgba(245,158,11,0.30)",
                          }}
                        >
                          <Shield className="h-3 w-3" />
                          מוחזק
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                className="w-full text-start transition-all active:scale-[0.99]"
                onClick={() => selectAppointment(a)}
              >
                <div
                  className="rounded-xl border p-4 space-y-2 card-premium"
                  style={{
                    borderColor: "hsl(var(--border))",
                    background: "hsl(var(--card))",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className="font-semibold text-base truncate"
                      style={{ color: "hsl(var(--foreground))" }}
                    >
                      {a.customerName}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={a.status} />
                      <ChevronRight
                        className="h-4 w-4"
                        style={{ color: "hsl(var(--muted-foreground))" }}
                      />
                    </div>
                  </div>
                  <p
                    className="text-sm font-medium truncate"
                    style={{ color: "hsl(var(--muted-foreground))" }}
                  >
                    {a.serviceName ?? "—"}
                  </p>
                  <div
                    className="flex items-center gap-2 text-xs"
                    style={{ color: "hsl(var(--muted-foreground))" }}
                  >
                    <Clock className="h-3 w-3 shrink-0" />
                    <span>
                      {formatDate(a.startTime)} · {formatTime(a.startTime)}
                    </span>
                    {a.depositStatus === "held" && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold border"
                        style={{
                          background: "rgba(245,158,11,0.10)",
                          color: "#B45309",
                          borderColor: "rgba(245,158,11,0.30)",
                        }}
                      >
                        <Shield className="h-3 w-3" />
                        פיקדון
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Appointment Detail
// ---------------------------------------------------------------------------

interface DetailProps {
  readonly appointment: AppointmentRow;
  readonly depositInfo: DepositInfo | null;
  readonly reminders: readonly ReminderInfo[];
  readonly noShowCount: number | null;
  readonly actionLoading: boolean;
  readonly onBack: () => void;
  readonly onComplete: () => void;
  readonly onNoShow: () => void;
  readonly onCancel: () => void;
  readonly onCapture: () => void;
}

function AppointmentDetail({
  appointment: a,
  depositInfo,
  reminders,
  noShowCount,
  actionLoading,
  onBack,
  onComplete,
  onNoShow,
  onCancel,
  onCapture,
}: DetailProps): ReactNode {
  return (
    <div className="space-y-4">
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium transition-colors min-h-[44px] px-3 rounded-lg"
        style={{
          color: "var(--brand-primary)",
          background: "rgba(124,58,237,0.06)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <ChevronRight className="h-4 w-4" />
        חזרה לרשימה
      </button>

      {/* Detail header */}
      <div
        className="rounded-xl p-4 border relative overflow-hidden"
        style={{
          background: "var(--brand-gradient-soft)",
          borderColor: "rgba(124,58,237,0.20)",
        }}
      >
        {/* gradient top strip */}
        <div
          className="absolute top-0 inset-x-0 h-0.5"
          style={{ background: "var(--brand-gradient)" }}
        />
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              className="text-lg font-bold"
              style={{ color: "hsl(var(--foreground))", fontFamily: "var(--font-display)" }}
            >
              {a.serviceName ?? "תור"}
            </h2>
            <p className="text-sm mt-0.5" style={{ color: "hsl(var(--muted-foreground))" }}>
              {formatDateTime(a.startTime)}
            </p>
          </div>
          <StatusBadge status={a.status} />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Customer card */}
        <Card
          style={{
            borderRadius: "var(--radius-lg)",
            borderColor: "hsl(var(--border))",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <CardContent className="p-4 space-y-3">
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--brand-primary)" }}
            >
              פרטי לקוח
            </h3>
            <p className="font-semibold text-base" style={{ color: "hsl(var(--foreground))" }}>
              {a.customerName}
            </p>
            {a.customerPhone && (
              <a
                href={`tel:${a.customerPhone}`}
                className="flex items-center gap-2 text-sm min-h-[44px] transition-colors"
                style={{ color: "var(--brand-primary)" }}
              >
                <Phone className="h-4 w-4 shrink-0" />
                {a.customerPhone}
              </a>
            )}
            {a.customerEmail && (
              <p className="text-sm" style={{ color: "hsl(var(--muted-foreground))" }}>
                {a.customerEmail}
              </p>
            )}
            {a.notes && (
              <p
                className="text-sm rounded-lg p-3"
                style={{
                  background: "hsl(var(--muted))",
                  color: "hsl(var(--muted-foreground))",
                  borderRadius: "var(--radius-md)",
                }}
              >
                {a.notes}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border"
                style={{
                  background: "hsl(var(--muted))",
                  color: "hsl(var(--muted-foreground))",
                  borderColor: "hsl(var(--border))",
                }}
              >
                {a.source}
              </span>
              {noShowCount !== null && noShowCount > 0 && (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border"
                  style={{
                    background: "rgba(239,68,68,0.10)",
                    color: "#DC2626",
                    borderColor: "rgba(239,68,68,0.25)",
                  }}
                >
                  <UserX className="h-3 w-3" />
                  {noShowCount} אי-הגעות
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Booking info card */}
        <Card
          style={{
            borderRadius: "var(--radius-lg)",
            borderColor: "hsl(var(--border))",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <CardContent className="p-4 space-y-3">
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--brand-primary)" }}
            >
              פרטי תור
            </h3>
            <div className="flex items-start gap-2">
              <CalendarDays
                className="h-4 w-4 mt-0.5 shrink-0"
                style={{ color: "hsl(var(--muted-foreground))" }}
              />
              <span className="text-sm" style={{ color: "hsl(var(--foreground))" }}>
                {formatDateTime(a.startTime)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock
                className="h-4 w-4 shrink-0"
                style={{ color: "hsl(var(--muted-foreground))" }}
              />
              <span className="text-sm" style={{ color: "hsl(var(--foreground))" }}>
                {formatTime(a.startTime)} — {formatTime(a.endTime)}
              </span>
            </div>
            {a.customerPhone && (
              <a
                href={`https://wa.me/${a.customerPhone.replace(/^0/, "972")}?text=${encodeURIComponent("שלום, לגבי התור שלך")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm min-h-[44px] transition-colors"
                style={{ color: "#16A34A" }}
              >
                <MessageCircle className="h-4 w-4" />
                שלח הודעה בוואטסאפ
              </a>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deposit section */}
      {depositInfo && depositInfo.status !== "none" && (
        <Card
          style={{
            borderRadius: "var(--radius-lg)",
            borderColor: "hsl(var(--border))",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <CardContent className="p-4 space-y-3">
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--brand-primary)" }}
            >
              פיקדון
            </h3>
            <div className="flex items-center gap-3">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border"
                style={
                  depositInfo.status === "held"
                    ? depositInfo.isWarning
                      ? { background: "rgba(239,68,68,0.10)", color: "#DC2626", borderColor: "rgba(239,68,68,0.25)" }
                      : { background: "rgba(124,58,237,0.10)", color: "var(--brand-primary)", borderColor: "rgba(124,58,237,0.25)" }
                    : depositInfo.status === "captured"
                      ? { background: "rgba(16,185,129,0.10)", color: "#059669", borderColor: "rgba(16,185,129,0.25)" }
                      : { background: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))", borderColor: "hsl(var(--border))" }
                }
              >
                {depositInfo.status === "held"
                  ? "מוחזק"
                  : depositInfo.status === "captured"
                    ? "נגבה"
                    : depositInfo.status === "released"
                      ? "שוחרר"
                      : depositInfo.status === "expired"
                        ? "פג תוקף"
                        : depositInfo.status}
              </span>
              {depositInfo.amountAgorot && (
                <span
                  className="text-sm font-mono font-semibold"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  {formatNIS(depositInfo.amountAgorot)}
                </span>
              )}
            </div>

            {depositInfo.status === "held" && (
              <>
                <div className="flex items-center gap-2">
                  {(depositInfo.isWarning || depositInfo.isExpired) && (
                    depositInfo.isExpired ? (
                      <X className="h-4 w-4 shrink-0" style={{ color: "#DC2626" }} />
                    ) : (
                      <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: "#D97706" }} />
                    )
                  )}
                  <span
                    className="text-sm"
                    style={{
                      color: depositInfo.isExpired
                        ? "#DC2626"
                        : depositInfo.isWarning
                          ? "#D97706"
                          : "hsl(var(--foreground))",
                    }}
                  >
                    {depositInfo.isExpired
                      ? "פג תוקף — לא ניתן לגבות"
                      : `הפיקדון תקף עוד ${depositInfo.daysRemaining} ימים`}
                  </span>
                </div>

                {/* Progress bar */}
                <div
                  className="w-full h-2 rounded-full overflow-hidden"
                  style={{ background: "hsl(var(--muted))" }}
                >
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.max(0, Math.min(100, (depositInfo.daysRemaining / 7) * 100))}%`,
                      background: depositInfo.isWarning
                        ? "linear-gradient(90deg, #F59E0B, #EF4444)"
                        : "var(--brand-gradient)",
                    }}
                  />
                </div>

                <Button
                  size="sm"
                  variant="destructive"
                  disabled={actionLoading || depositInfo.isExpired}
                  onClick={onCapture}
                  className="min-h-[44px]"
                  style={{ borderRadius: "var(--radius-md)" }}
                >
                  גבה פיקדון
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reminders section */}
      {reminders.length > 0 && (
        <Card
          style={{
            borderRadius: "var(--radius-lg)",
            borderColor: "hsl(var(--border))",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <CardContent className="p-4 space-y-3">
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ color: "var(--brand-primary)" }}
            >
              תזכורות
            </h3>
            <div className="space-y-2">
              {reminders.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between text-sm py-1"
                >
                  <span style={{ color: "hsl(var(--foreground))" }}>
                    {REMINDER_TYPE_LABELS[r.reminderType] ?? r.reminderType} לפני
                  </span>
                  <Badge
                    variant={
                      r.status === "sent"
                        ? "outline"
                        : r.status === "failed"
                          ? "destructive"
                          : "secondary"
                    }
                  >
                    {REMINDER_STATUS_LABELS[r.status] ?? r.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action buttons */}
      {a.status === "confirmed" && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={actionLoading}
            onClick={onComplete}
            className="min-h-[44px] gap-1.5 shadow-sm"
            style={{
              background: "var(--brand-gradient)",
              border: "none",
              borderRadius: "var(--radius-md)",
            }}
          >
            <Check className="h-4 w-4" />
            הושלם
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={actionLoading}
            onClick={onNoShow}
            className="min-h-[44px] gap-1.5"
            style={{ borderRadius: "var(--radius-md)" }}
          >
            <UserX className="h-4 w-4" />
            לא הגיע
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={actionLoading}
            onClick={onCancel}
            className="min-h-[44px] gap-1.5"
            style={{ borderRadius: "var(--radius-md)" }}
          >
            <X className="h-4 w-4" />
            בטל
          </Button>
        </div>
      )}
    </div>
  );
}
